import { Checkbox, useCheckboxContext } from "@ark-ui/react";
import { isBrowser } from "@base/lib/utils";
import { mdiCheck } from "@mdi/js";
import Icon from "@mdi/react";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

export const controlVariants = cva("inline-flex items-center justify-center border rounded-2", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-[18px] h-[18px]",
      lg: "w-5 h-5",
    },
    color: {
      primary: "",
      secondary: "",
    },
    disabled: {
      true: "bg-gray-10 text-gray-300 border-gray-200",
      false: "border-gray-500 bg-white text-white",
    },
  },
  compoundVariants: [
    {
      color: "primary",
      disabled: false,
      class: "[&:is([data-state='checked'])]:bg-primary-500 [&:is([data-state='checked'])]:border-none",
    },
    {
      color: "secondary",
      disabled: false,
      class: "[&:is([data-state='checked'])]:bg-blue-500 [&:is([data-state='checked'])]:border-none",
    },
  ],
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

interface controlVariantProps extends VariantProps<typeof controlVariants> {}
type Props = Omit<ComponentProps<typeof Checkbox.Control>, "className">;

const Control = ({ children, ...rest }: Props) => {
  const { getRootProps, disabled } = useCheckboxContext();
  const rootId = getRootProps().id;

  const rootElem = (isBrowser && rootId && document.getElementById(rootId)) || null;

  const size = (rootElem?.dataset.size || "md") as controlVariantProps["size"];
  const color = (rootElem?.dataset.color || "primary") as "primary" | "secondary";

  return (
    <Checkbox.Control {...rest} className={controlVariants({ size, color, disabled })}>
      <Checkbox.Indicator>
        {children ?? <Icon path={mdiCheck} size={size === "lg" ? "16px" : size === "md" ? "14px" : "12px"} />}
      </Checkbox.Indicator>
    </Checkbox.Control>
  );
};

export default Control;
